import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { RootState } from '../redux/store';
import { BildirimSatiri, BildirimService } from '../services/BildirimService';
import { KTIcon } from '../_metronic/helpers/components/KTIcon';
import { COLUMN_FILTER_EMPTY_VALUE } from './ColumnFilter/ColumnFilterTypes';
import { PO_ESLESME_DEGISTI } from '../helpers/poGuncellemeDurumu';
import { sayaclaraCevir, sayaclariOku, sayaclariYaz, yeniBildirimVar, zilSesiCal } from '../helpers/bildirimZili';

/** Zilin göründüğü menüler: en az birine yetkisi olan görür. Satırların yetkisini sunucu ayrıca denetler. */
export const YURTICI_LISTE_MENUSU = 'Kargo Modülü > Yurtiçi Kargo > Siparişler';
const PO_ESLESTIRME_MENUSU = 'Genel Tanımlamalar > PO Eşleştirme';
const EK_MESAI_MENUSU = 'Kargo Modülü > Nakliye > Ek Mesai Talepleri';
const YENILEME_MS = 2 * 60 * 1000;
const SALLANMA_MS = 3000;

/** Yeni bildirim türü buraya eklenir: metin, renk ve tıklayınca açılan süzülmüş sayfa. */
const SATIRLAR: Record<string, { metin: (adet: number) => React.ReactNode, sinif: string, ikonRenk: string, hedef: string, filtre: any[] }> = {
    PO_ESLESME: {
        metin: adet => <>{adet} kaydın <b>Bölüm / PO / PO Number eşleşmesi</b> yapılamadı</>,
        sinif: 'bg-light-danger',
        ikonRenk: 'text-danger',
        hedef: '/yurticicargo/list',
        filtre: [{ column: 'poName', values: [COLUMN_FILTER_EMPTY_VALUE], valueLabels: ['(Boş)'] }],
    },
    PO_BITIS: {
        metin: adet => <>{adet} PO Eşleştirme kaydının <b>bitiş tarihi</b> yaklaştı</>,
        sinif: 'bg-light-primary',
        ikonRenk: 'text-primary',
        hedef: '/companypo/list',
        filtre: [{ column: 'bitisUyarisi', values: ['1'], valueLabels: ['15 gün içinde bitiyor ya da geçti, yenisi yok'] }],
    },
    // #514: talebi girenin ek mesai davetleri; tek talepse pencere doğrudan açılır (MesaiTalebiList, state.talepIdleri).
    EK_MESAI_RED: {
        metin: adet => <>{adet} kişi ek mesai davetini <b>reddetti</b>; yerine personel seçiniz</>,
        sinif: 'bg-light-danger',
        ikonRenk: 'text-danger',
        hedef: '/mesaitalebi/list',
        filtre: [],
    },
    EK_MESAI_REVIZE: {
        metin: adet => <>{adet} ek mesai talebinde <b>revize istendi</b>; düzeltip kaydediniz</>,
        sinif: 'bg-light-danger',
        ikonRenk: 'text-danger',
        hedef: '/mesaitalebi/list',
        filtre: [],
    },
    EK_MESAI_YANIT_YOK: {
        metin: adet => <>{adet} kişi ek mesai davetine <b>yanıt vermedi</b></>,
        sinif: 'bg-light-warning',
        ikonRenk: 'text-warning',
        hedef: '/mesaitalebi/list',
        filtre: [],
    },
};

/**
 * Üst bardaki zil: genel bildirimler (30.10.2026, #512; eski tek satırlık PO eşleşme zilinin yerine). Panelde her tür bir satır;
 * tıklayınca ilgili sayfa süzülmüş açılır. Yeni kayıt düşünce (adet arttı ya da yeni satır) zil sesli uyarır ve sallanır.
 * Sayı firma değişince, sayfa değişince, eşleştirme değişince ve 2 dakikada bir yenilenir.
 */
const BildirimZili = () => {
    const auth = useSelector((state: RootState) => state.auth);
    const navigate = useNavigate();
    const location = useLocation();
    const [satirlar, setSatirlar] = useState<BildirimSatiri[]>([]);
    const [acik, setAcik] = useState(false);
    const [sallaniyor, setSallaniyor] = useState(false);
    const kap = useRef<HTMLDivElement>(null);

    const menuler = auth.user.user?.data.authorizedMenus;
    const yetkili = menuler?.some(x => x.menuName == YURTICI_LISTE_MENUSU || x.menuName == PO_ESLESTIRME_MENUSU || x.menuName == EK_MESAI_MENUSU) ?? false;
    const firmaId = auth.selectedCompany?.companyId;

    const yenile = useCallback(async () => {
        if (!yetkili)
            return;

        try {
            const response = await BildirimService.ozet();

            if (!response.isSuccess)
                return;

            const gelen = response.data ?? [];
            const simdi = sayaclaraCevir(gelen);
            const onceki = sayaclariOku(firmaId);

            setSatirlar(gelen);

            if (yeniBildirimVar(onceki, simdi)) {
                zilSesiCal();
                setSallaniyor(true);
                setTimeout(() => setSallaniyor(false), SALLANMA_MS);
            }

            sayaclariYaz(firmaId, simdi);
        } catch {
            // Zil sessizce bekler; bir sonraki yenilemede tekrar dener.
        }
    }, [yetkili, firmaId]);

    useEffect(() => {
        yenile();
    }, [yenile, location.pathname]);

    useEffect(() => {
        const zamanlayici = setInterval(yenile, YENILEME_MS);
        return () => clearInterval(zamanlayici);
    }, [yenile]);

    // Eşleştirme değişince (toplu/elle güncelleme, kilit kaldırma) beklemeden yenile.
    useEffect(() => {
        window.addEventListener(PO_ESLESME_DEGISTI, yenile);
        return () => window.removeEventListener(PO_ESLESME_DEGISTI, yenile);
    }, [yenile]);

    // Panel dışına tıklanınca kapanır.
    useEffect(() => {
        if (!acik)
            return;
        const disari = (e: MouseEvent) => {
            if (kap.current && !kap.current.contains(e.target as Node))
                setAcik(false);
        };
        document.addEventListener('mousedown', disari);
        return () => document.removeEventListener('mousedown', disari);
    }, [acik]);

    if (!yetkili)
        return null;

    const gosterilecek = satirlar.filter(x => SATIRLAR[x.kod]);

    const satirAc = (satir: BildirimSatiri) => {
        const s = SATIRLAR[satir.kod];
        setAcik(false);
        navigate(s.hedef, { state: { columnFilters: s.filtre, talepIdleri: satir.idler ?? undefined } });
    }

    return (
        <div ref={kap} className='position-relative me-6'>
            <style>{`
                @keyframes bildirim-zili-sallan { 0%,100% { transform: rotate(0); } 15% { transform: rotate(18deg); } 30% { transform: rotate(-16deg); } 45% { transform: rotate(12deg); } 60% { transform: rotate(-9deg); } 75% { transform: rotate(5deg); } 90% { transform: rotate(-3deg); } }
                .bildirim-zili-sallan { animation: bildirim-zili-sallan 1s ease-in-out 3; transform-origin: 50% 10%; display: inline-block; }
                @media (prefers-reduced-motion: reduce) { .bildirim-zili-sallan { animation: none; } }
            `}</style>

            <button
                type='button'
                className='btn btn-icon btn-active-light-primary position-relative w-40px h-40px'
                onClick={() => setAcik(!acik)}
                aria-label={`Bildirimler: ${gosterilecek.length}`}
                aria-expanded={acik}
            >
                <span className={sallaniyor ? 'bildirim-zili-sallan' : undefined}>
                    <KTIcon iconName='notification-on' iconType='outline' className='fs-1 text-gray-700' />
                </span>
                {/* Rozet zilin sağ üst köşesinde: bildirim satırı sayısı. */}
                {
                    gosterilecek.length > 0 && (
                        <span className='position-absolute badge badge-danger rounded-pill' style={{ top: 3, left: 23, minWidth: 18, height: 18, fontSize: 10, lineHeight: '18px', padding: '0 5px', color: '#fff' }}>
                            {gosterilecek.length}
                        </span>
                    )
                }
            </button>

            {
                acik && (
                    <div className='position-absolute bg-body shadow rounded p-4' style={{ top: 46, right: 0, width: 400, maxWidth: '85vw', zIndex: 105 }}>
                        <div className='d-flex align-items-center justify-content-between mb-3'>
                            <span className='fs-5 fw-bold text-gray-800'>Bildirimler</span>
                            <button type='button' className='btn btn-icon btn-sm btn-active-light-primary w-30px h-30px' onClick={yenile} aria-label='Yenile' title='Yenile'>
                                <i className='fa fa-sync-alt fs-7' />
                            </button>
                        </div>

                        {
                            gosterilecek.length == 0
                                ? <div className='text-muted text-center py-6'>Yeni bildirim yok.</div>
                                : (
                                    <div className='d-flex flex-column gap-2'>
                                        {
                                            gosterilecek.map(x => {
                                                const s = SATIRLAR[x.kod];
                                                return (
                                                    <button
                                                        key={x.kod}
                                                        type='button'
                                                        onClick={() => satirAc(x)}
                                                        className={'btn text-start d-flex align-items-center gap-3 p-3 rounded ' + s.sinif}
                                                        style={{ whiteSpace: 'normal' }}
                                                    >
                                                        <KTIcon iconName='information-5' iconType='duotone' className={'fs-2x flex-shrink-0 ' + s.ikonRenk} />
                                                        <span className='fs-6 fw-normal text-gray-800'>{s.metin(x.adet)}</span>
                                                    </button>
                                                );
                                            })
                                        }
                                    </div>
                                )
                        }
                    </div>
                )
            }
        </div>
    );
}

export default BildirimZili;
